import { useId } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import { cn } from "./cn";

export function Field({
  label,
  hint,
  error,
  suffix,
  action,
  className,
  inputClassName,
  ...props
}: {
  label: string;
  hint?: string;
  error?: string;
  suffix?: ReactNode;
  action?: ReactNode;
  className?: string;
  inputClassName?: string;
} & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  const describedBy = hint || error ? `${id}-desc` : undefined;

  return (
    <div className={cn("w-full", className)}>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-xs font-semibold text-ink-2">
          {label}
        </label>
        {action}
      </div>
      <div
        className={cn(
          "flex items-center rounded-field border bg-surface transition-colors duration-150",
          "focus-within:border-brand focus-within:ring-4 focus-within:ring-brand/12",
          error ? "border-danger" : "border-line",
        )}
      >
        <input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          {...props}
          className={cn(
            "h-11 w-full min-w-0 bg-transparent px-3.5 text-base text-ink outline-none placeholder:text-ink-4",
            inputClassName,
          )}
        />
        {suffix ? (
          <span className="pr-3.5 pl-2 text-xs font-semibold text-ink-4">{suffix}</span>
        ) : null}
      </div>
      {hint || error ? (
        <p
          id={describedBy}
          className={cn("mt-1.5 text-2xs", error ? "text-danger-ink" : "text-ink-4")}
        >
          {error ?? hint}
        </p>
      ) : null}
    </div>
  );
}
